import { Progress } from "@/components/ui/progress";
import {
  GraduationCap, Award, Flame, Target, ScrollText, CheckCircle2,
  TrendingUp, Trophy
} from "lucide-react";

/**
 * AcademyStats - Hero header with progress statistics
 * Shows CEUs, streak, overall progress, and achievement counts
 */
export default function AcademyStats({
  totalCEUsEarned = 0,
  totalCEUsAvailable = 0,
  streak = 0,
  completedCount = 0,
  totalCourses = 0,
  completedRequired = 0,
  requiredCount = 0,
  completedELNEC = 0,
  completedCAHPS = 0,
  cahpsTotal = 0,
  earnedAchievements = 0,
  totalAchievements = 0
}) {
  const overallProgress = totalCourses > 0 ? Math.round((completedCount / totalCourses) * 100) : 0;

  return (
    <div className="bg-gradient-to-r from-primary via-primary/90 to-indigo-600 text-white py-12">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-white/10 rounded-2xl backdrop-blur">
              <GraduationCap className="w-10 h-10" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Hospice Academy</h1>
              <p className="text-white/80">ELNEC-Aligned Training • CEU Credits • CAHPS Improvement</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* CEU Counter */}
            <div className="bg-white/10 rounded-xl px-4 py-2 backdrop-blur text-center">
              <Award className="w-5 h-5 mx-auto mb-1" />
              <span className="text-2xl font-bold">{totalCEUsEarned.toFixed(1)}</span>
              <p className="text-xs text-white/70">CEUs Earned</p>
            </div>
            
            {/* Streak Counter */}
            {streak > 0 && (
              <div className="bg-white/10 rounded-xl px-4 py-2 backdrop-blur text-center">
                <Flame className="w-5 h-5 mx-auto mb-1 text-orange-300" />
                <span className="text-2xl font-bold">{streak}</span>
                <p className="text-xs text-white/70">Day Streak</p>
              </div>
            )}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-8">
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-1">
              <Target className="w-4 h-4" />
              Overall Progress
            </div>
            <div className="text-2xl font-bold">{overallProgress}%</div>
            <Progress value={overallProgress} className="mt-2 h-2 bg-white/20" />
          </div>
          
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-1">
              <ScrollText className="w-4 h-4" />
              ELNEC Core
            </div>
            <div className="text-2xl font-bold">{completedELNEC} / 6</div>
            <p className="text-xs text-white/60 mt-1">{completedELNEC === 6 ? "✓ Complete" : "In Progress"}</p>
          </div>
          
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-1">
              <CheckCircle2 className="w-4 h-4" />
              Courses Done
            </div>
            <div className="text-2xl font-bold">{completedCount} / {totalCourses}</div>
            <p className="text-xs text-white/60 mt-1">{completedRequired}/{requiredCount} required</p>
          </div>
          
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-1">
              <TrendingUp className="w-4 h-4" />
              CAHPS Training
            </div>
            <div className="text-2xl font-bold">{completedCAHPS} / {cahpsTotal}</div>
            <p className="text-xs text-white/60 mt-1">Score improvement</p>
          </div>
          
          <div className="bg-white/10 rounded-xl p-4 backdrop-blur">
            <div className="flex items-center gap-2 text-white/70 text-sm mb-1">
              <Trophy className="w-4 h-4" />
              Achievements
            </div>
            <div className="text-2xl font-bold">{earnedAchievements} / {totalAchievements}</div>
            <p className="text-xs text-white/60 mt-1">Badges earned</p>
          </div>
        </div>
      </div>
    </div>
  );
}
